/**
 * Liveness: telling a finished encode from a stream still being written, and
 * describing the latter in terms a native asset resolver can act on alone.
 *
 * Everything else in this package assumes a media playlist is read once. That
 * is true of VOD and is why the VOD path is suspension-safe at all: the wrapper
 * munges at load, hands the finished text to whatever serves it, and JavaScript
 * is off the request path for the rest of playback. A live playlist breaks the
 * assumption at its root — the segment list changes every target-duration, so
 * something must keep re-fetching, re-decrypting and re-rewriting it for as
 * long as the stream runs.
 *
 * That "something" cannot be this package. On a locked phone the WebView is
 * frozen while the native player keeps pulling segments, so a refresh that
 * needs JavaScript is a refresh that does not happen. It has to be the serving
 * layer, natively, which is why {@link LivePlaylistSpec} is plain data: URLs,
 * a key, a cadence. Hand that across a bridge and the resolver can do the whole
 * job — fetch, sniff, decrypt, rewrite, serve — without waking anyone.
 *
 * Nothing in this repository produces live output (`-hls_playlist_type vod`
 * writes the playlist only at the end, which is why coming-soon polling
 * exists); the sources are third-party live streams. {@link resolveLivePlaylist}
 * is the per-request step every serving layer performs, and
 * `player-web`'s `BlobServeStrategy` is the first to implement
 * `serveLive` on top of it.
 */
import { bytesToHex } from '../pipeline/decrypt.js';
import { PipelineError, fetchMaybeEncrypted } from '../pipeline/fetch.js';
import { scanMediaPlaylist, } from '../pipeline/media-scan.js';
import { rewriteMediaPlaylist } from '../pipeline/rewrite-media.js';
export function describeLiveness(playlistText) {
    return livenessOf(scanMediaPlaylist(playlistText));
}
/** {@link describeLiveness} from a scan the caller already made. */
export function livenessOf(scan) {
    return {
        isLive: scan.isLive,
        targetDurationSec: scan.targetDurationSec,
        mediaSequence: scan.mediaSequence,
    };
}
/**
 * One read of a live media playlist, served-ready: fetch `spec.url`, decrypt
 * it when it is LMCENC, apply the media rewrite. Call it once per engine
 * request for the URL `serveLive(spec)` returned.
 *
 * Deliberately one read and not a loop. The engine already re-requests a live
 * playlist on the cadence HLS prescribes — every target duration, half that
 * when a read found nothing new — and answering those requests is the whole
 * job; a timer of our own beside it would only race the engine's. `refreshSec`
 * is for a serving layer that has to pre-fetch because it cannot answer a
 * request asynchronously.
 *
 * Failures are {@link PipelineError}s carrying the upstream HTTP `status` when
 * there was one, so a caller can hand the engine the status a direct request
 * would have met and let the engine's own retry policy handle it.
 *
 * This is the reference a native resolver ports: the same three steps, in the
 * same order, with the same failures.
 */
export async function resolveLivePlaylist(spec, options) {
    const asset = await fetchMaybeEncrypted(spec.url, {
        fetchImpl: options.fetchImpl,
        signal: options.signal,
        subtle: options.subtle,
        keyHex: spec.keyBytes ? bytesToHex(spec.keyBytes) : undefined,
        expect: 'playlist',
    });
    if (!spec.keyUri && scanMediaPlaylist(asset.text).hasAes128Key) {
        throw new PipelineError('key-required', `${spec.url} declares AES-128 segments but no session key was supplied`, { url: spec.url });
    }
    return rewriteMediaPlaylist(asset.text, {
        playlistUrl: spec.baseUrl,
        keyUri: spec.keyUri,
    });
}
//# sourceMappingURL=live.js.map